/**
 * SecureStorageService
 *
 * Provides encrypted storage for sensitive data in sessionStorage and localStorage.
 * Uses Web Crypto API with AES-GCM encryption for HIPAA compliance.
 *
 * Encryption key is derived from user session to ensure data is inaccessible
 * after session expiration.
 */

interface EncryptionResult {
  encrypted: string;
  iv: string;
}

class SecureStorageService {
  private static instance: SecureStorageService;
  private encryptionKey: CryptoKey | null = null;
  private keyDerivationSalt: Uint8Array | null = null;
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // 96 bits for GCM
  private encryptionAvailable: boolean | null = null; // Cache encryption availability check
  // Token cache for synchronous access (used by DICOM Web client)
  // Made public for direct access during session restoration
  public tokenCache: { authToken: string | null; localToken: string | null } = {
    authToken: null,
    localToken: null,
  };

  private constructor() {
    // Initialize key derivation on first use
    // Try to load tokens synchronously from unencrypted storage as fallback
    // This ensures tokens are available immediately for DICOM Web client
    this.tokenCache.authToken = sessionStorage.getItem('auth_token');
    this.tokenCache.localToken = localStorage.getItem('token');

    // Also check for encrypted tokens (they'll be decrypted async, but cache will be updated)
    // For now, we have the unencrypted fallback which should work during migration
    // console.log('[SecureStorage] Token cache initialized:', {
    //   hasAuthToken: !!this.tokenCache.authToken,
    //   hasLocalToken: !!this.tokenCache.localToken
    // });
  }

  public static getInstance(): SecureStorageService {
    if (!SecureStorageService.instance) {
      SecureStorageService.instance = new SecureStorageService();
    }
    return SecureStorageService.instance;
  }

  /**
   * Derives an encryption key from user session data
   * This ensures the key is unique per session and lost when session expires
   */
  private async deriveKeyFromSession(sessionIdentifier: string, salt?: Uint8Array): Promise<CryptoKey> {
    // Use session identifier (user ID + session timestamp) to derive key
    const encoder = new TextEncoder();
    const data = encoder.encode(sessionIdentifier);

    // Import the data as a key for PBKDF2
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      data,
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    // Use provided salt or generate/store one
    let saltToUse: Uint8Array;
    if (salt) {
      saltToUse = salt;
    } else {
      if (!this.keyDerivationSalt) {
        this.keyDerivationSalt = crypto.getRandomValues(new Uint8Array(16));
        // Store salt in sessionStorage (not encrypted, but unique per session)
        sessionStorage.setItem('_enc_salt', btoa(String.fromCharCode(...this.keyDerivationSalt)));
      }
      saltToUse = this.keyDerivationSalt;
    }

    // Derive the encryption key using PBKDF2
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltToUse as BufferSource,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH,
      },
      false,
      ['encrypt', 'decrypt']
    );

    return derivedKey;
  }

  /**
   * Gets or creates the encryption key for the current session
   */
  private async getEncryptionKey(): Promise<CryptoKey> {
    if (this.encryptionKey) {
      return this.encryptionKey;
    }

    // Try to get session identifier from sessionStorage
    // For session data itself, we need a different approach - use user ID from unencrypted session
    // For other data, we can use the session identifier
    let sessionIdentifier: string | null = null;
    let salt: Uint8Array | undefined;

    try {
      // Try to get salt from storage
      const saltData = sessionStorage.getItem('_enc_salt');
      if (saltData) {
        try {
          salt = Uint8Array.from(atob(saltData), c => c.charCodeAt(0));
          this.keyDerivationSalt = salt;
        } catch (e) {
          console.warn('SecureStorage: Failed to parse salt from storage:', e);
        }
      }

      // CRITICAL: Try multiple methods to get token for key derivation
      // Priority: Token hash > Token from cache/storage > Session data (only if no token)
      // This ensures we ALWAYS use the same key derivation method for encrypted data
      let token: string | null = this.tokenCache.authToken || this.tokenCache.localToken;
      let tokenHash: string | null = null;

      // Step 1: Check for token hash first (for encrypted tokens - most reliable)
      tokenHash = sessionStorage.getItem('_auth_token_hash') ||
                 sessionStorage.getItem('_refresh_token_hash') ||
                 localStorage.getItem('_token_hash');

      if (tokenHash) {
        // Use hash to create identifier (we can't decrypt without key, but hash is enough for derivation)
        // Use first 16 chars to match the format when we hash the token directly
        // Validate hash format (should be 64 hex characters for SHA-256)
        if (tokenHash.length !== 64 || !/^[0-9a-f]+$/i.test(tokenHash)) {
          console.warn('[SecureStorage] Stored token hash has invalid format, will try to regenerate', {
            hashLength: tokenHash.length,
            hashPrefix: tokenHash.substring(0, 10)
          });
          // Clear invalid hash and try to get token from unencrypted storage
          sessionStorage.removeItem('_auth_token_hash');
          sessionStorage.removeItem('_refresh_token_hash');
          localStorage.removeItem('_token_hash');
          tokenHash = null;
          // Fall through to token retrieval logic
        } else {
          sessionIdentifier = `token_${tokenHash.substring(0, 16)}`;
          // console.log('[SecureStorage] Using stored token hash for key derivation', {
          //   hashLength: tokenHash.length,
          //   identifierPrefix: sessionIdentifier.substring(0, 20)
          // });
        }
      }

      // If we cleared invalid hash or didn't have one, try to get token
      if (!sessionIdentifier && token) {
        // Step 2: If we have the actual token, hash it and STORE the hash immediately
        // This ensures the hash is available for future key derivation
        try {
          const hashedToken = await this.hashString(token);
          sessionIdentifier = `token_${hashedToken.substring(0, 16)}`;

          // Store the hash IMMEDIATELY during key derivation (before encryption)
          // This ensures consistency between encryption and decryption
          if (this.tokenCache.authToken) {
            sessionStorage.setItem('_auth_token_hash', hashedToken);
          } else if (this.tokenCache.localToken) {
            localStorage.setItem('_token_hash', hashedToken);
          }

          // console.log('[SecureStorage] Using token for encryption key derivation and storing hash', {
          //   identifierPrefix: sessionIdentifier.substring(0, 20),
          //   hashStored: true
          // });
        } catch (hashError) {
          console.warn('SecureStorage: Failed to hash token:', hashError);
        }
      } else {
        // Step 3: Try to get token from unencrypted storage (for migration/fallback)
        token = sessionStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token) {
          // Update cache if found
          if (sessionStorage.getItem('auth_token')) {
            this.tokenCache.authToken = token;
          } else if (localStorage.getItem('token')) {
            this.tokenCache.localToken = token;
          }
          // Hash the token we just found and STORE the hash immediately
          try {
            const hashedToken = await this.hashString(token);
            sessionIdentifier = `token_${hashedToken.substring(0, 16)}`;

            // Store the hash IMMEDIATELY
            if (sessionStorage.getItem('auth_token')) {
              sessionStorage.setItem('_auth_token_hash', hashedToken);
            } else if (localStorage.getItem('token')) {
              localStorage.setItem('_token_hash', hashedToken);
            }

            // console.log('[SecureStorage] Using token from unencrypted storage for key derivation and storing hash', {
            //   identifierPrefix: sessionIdentifier.substring(0, 20),
            //   hashStored: true
            // });
          } catch (hashError) {
            console.warn('SecureStorage: Failed to hash token from storage:', hashError);
          }
        }
      }

      // Step 4: Only use session data if we don't have a token/hash (for unencrypted sessions)
      // This ensures encrypted data always uses token-based key derivation
      if (!sessionIdentifier) {
        const sessionData = sessionStorage.getItem('ohif_session');
        if (sessionData) {
          try {
            const session = JSON.parse(sessionData);
            // Only use session-based identifier if session is unencrypted and we have no token
            // Create unique identifier from user ID and session creation time
            if (session.user?.id) {
              sessionIdentifier = `${session.user.id}_${session.createdAt || Date.now()}`;
              // console.log('[SecureStorage] Using session data for key derivation (no token available)');
            }
          } catch (e) {
            // Session is encrypted or invalid JSON - this is expected if we have encrypted data
            // console.log('[SecureStorage] Session data is encrypted or invalid, token-based key derivation should be used');
          }
        }
      }

      // If still no identifier, use a browser-based unique identifier
      if (!sessionIdentifier) {
        // Create a unique session identifier based on browser fingerprint
        // This ensures encryption works even before login
        const browserFingerprint = await this.getBrowserFingerprint();
        sessionIdentifier = `session_${browserFingerprint}_${Date.now()}`;
        // console.log('[SecureStorage] Using browser fingerprint for session identifier');
      }
    } catch (error) {
      console.error('SecureStorage: Error getting session identifier for key derivation:', error);
      // Fallback to browser fingerprint
      try {
        const browserFingerprint = await this.getBrowserFingerprint();
        sessionIdentifier = `session_${browserFingerprint}_${Date.now()}`;
      } catch (fpError) {
        // Last resort: use timestamp-based identifier
        sessionIdentifier = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
        console.warn('SecureStorage: Using fallback session identifier');
      }
    }

    if (!sessionIdentifier) {
      throw new Error('SecureStorage: Failed to create session identifier for encryption');
    }

    try {
      // console.log('[SecureStorage] Deriving encryption key with:', {
      //   sessionIdentifier: sessionIdentifier.substring(0, 50) + '...', // Log first 50 chars
      //   hasSalt: !!salt,
      //   saltLength: salt?.length,
      //   saltFromStorage: !!sessionStorage.getItem('_enc_salt')
      // });

      this.encryptionKey = await this.deriveKeyFromSession(sessionIdentifier, salt);
      // console.log('[SecureStorage] Encryption key derived successfully');
      return this.encryptionKey;
    } catch (error) {
      console.error('SecureStorage: Failed to derive encryption key:', error);
      throw new Error('SecureStorage: Encryption key derivation failed - encryption unavailable');
    }
  }

  /**
   * Gets a browser fingerprint for session identification
   * This is used when no user session or token is available yet
   */
  private async getBrowserFingerprint(): Promise<string> {
    const components: string[] = [
      navigator.userAgent,
      navigator.language,
      screen.width.toString(),
      screen.height.toString(),
      new Date().getTimezoneOffset().toString(),
    ];

    const fingerprint = components.join('|');
    // Hash the fingerprint for privacy
    return await this.hashString(fingerprint);
  }

  /**
   * Hashes a string using SHA-256
   */
  private async hashString(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Encrypts data before storage
   */
  private async encrypt(data: string): Promise<EncryptionResult> {
    try {
      const key = await this.getEncryptionKey();
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);

      // Generate a random IV for each encryption
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      // Encrypt the data
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
        },
        key,
        dataBuffer
      );

      // Convert to base64 for storage
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray));
      const ivBase64 = btoa(String.fromCharCode(...iv));

      return {
        encrypted: encryptedBase64,
        iv: ivBase64,
      };
    } catch (error) {
      console.error('SecureStorage: Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts data after retrieval
   */
  private async decrypt(encryptedData: string, iv: string, suppressErrorLog: boolean = false): Promise<string> {
    try {
      const key = await this.getEncryptionKey();

      // Convert from base64
      const encryptedArray = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      const ivArray = Uint8Array.from(atob(iv), c => c.charCodeAt(0));

      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: ivArray,
        },
        key,
        encryptedArray
      );

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      // Only log detailed error if not suppressed (for key mismatch cases we handle gracefully)
      if (!suppressErrorLog) {
        console.error('SecureStorage: Decryption failed:', error);
        console.error('SecureStorage: This usually means the encryption key has changed or the data was encrypted with a different key');
        console.error('SecureStorage: Possible causes:');
        console.error('  - Token hash mismatch');
        console.error('  - Salt mismatch');
        console.error('  - Session identifier changed');
        console.error('  - Data encrypted with different key');

        // Log current state for debugging
        const tokenHash = sessionStorage.getItem('_auth_token_hash') ||
                         sessionStorage.getItem('_refresh_token_hash') ||
                         localStorage.getItem('_token_hash');
        const hasToken = !!(this.tokenCache.authToken || this.tokenCache.localToken);
        console.error('SecureStorage: Debug info:', {
          hasTokenHash: !!tokenHash,
          hasToken: hasToken,
          hasSalt: !!this.keyDerivationSalt,
          saltInStorage: !!sessionStorage.getItem('_enc_salt')
        });
      }

      throw new Error('Failed to decrypt data - session may have expired or encryption key mismatch');
    }
  }

  /**
   * Checks if Web Crypto API is available
   */
  private isWebCryptoAvailable(): boolean {
    if (this.encryptionAvailable !== null) {
      return this.encryptionAvailable;
    }
    this.encryptionAvailable = !!(typeof crypto !== 'undefined' && crypto.subtle);
    if (!this.encryptionAvailable) {
      console.warn('[SecureStorage] Web Crypto API is not available (likely HTTP environment). Using unencrypted storage.');
    }
    return this.encryptionAvailable;
  }

  /**
   * Stores encrypted data in sessionStorage
   * Falls back to unencrypted storage if Web Crypto API is not available
   */
  public async setSessionItem(key: string, value: string): Promise<void> {
    const viewerSessionKeys = ['currentJobId', 'currentStudyUid', 'id', 'lastViewedStudyInstanceUIDs'];

    // Check if Web Crypto API is available
    if (!this.isWebCryptoAvailable()) {
      // Fallback to unencrypted storage when encryption is not available
      console.warn(`[SecureStorage] Storing ${key} unencrypted (Web Crypto API not available)`);
      sessionStorage.setItem(key, value);

      // Update token cache
      if (key === 'auth_token') {
        this.tokenCache.authToken = value;
      }
      return;
    }

    // Viewer routing keys must be readable synchronously: opening a study calls navigate() right
    // after firing setSessionItem() without awaiting. defaultRouteInit reads these on the next
    // frame; if we only wrote after await encrypt(), Safari/Firefox often saw empty storage and
    // redirected with window.location.href to /submissions (full reload).
    if (viewerSessionKeys.includes(key)) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        console.warn(`[SecureStorage] Failed immediate session write for ${key}:`, e);
      }
    }

    try {
      const encrypted = await this.encrypt(value);
      const storageValue = JSON.stringify({
        encrypted: encrypted.encrypted,
        iv: encrypted.iv,
        timestamp: Date.now(),
      });
      sessionStorage.setItem(`encrypted_${key}`, storageValue);

      // For tokens, store a hash for key derivation AND keep unencrypted version as fallback
      // This allows us to derive the key on refresh and restore session if encrypted data is cleared
      const tokenKeys = ['auth_token', 'refresh_token'];
      // For viewer session keys, keep unencrypted fallback to persist across page refreshes
      // even if decryption fails due to key mismatch (plain key was also set synchronously above)
      if (tokenKeys.includes(key)) {
        // Store token hash for key derivation
        const tokenHash = await this.hashString(value);
        sessionStorage.setItem(`_${key}_hash`, tokenHash); // Store full hash
        // Keep unencrypted token as fallback for key derivation and session restoration
        sessionStorage.setItem(key, value);
        // console.log(`[SecureStorage] Stored token hash and unencrypted fallback for key derivation: ${key}`);
      } else if (viewerSessionKeys.includes(key)) {
        // Keep unencrypted fallback for viewer session keys to ensure persistence across refreshes
        // This prevents data loss if decryption fails due to key mismatch
        sessionStorage.setItem(key, value);
        // console.log(`[SecureStorage] Stored encrypted ${key} with unencrypted fallback for persistence`);
      } else {
        // For other non-token sensitive data, remove unencrypted version after encryption
        const unencryptedValue = sessionStorage.getItem(key);
        if (unencryptedValue && unencryptedValue !== value) {
          // Only remove if it's different (might be old unencrypted data)
          sessionStorage.removeItem(key);
        }
      }

      // Update token cache for synchronous access
      if (key === 'auth_token') {
        this.tokenCache.authToken = value;
      }

      // console.log(`[SecureStorage] Successfully stored encrypted session item: ${key}`);
    } catch (error) {
      console.error(`SecureStorage: Failed to store encrypted session item ${key}:`, error);
      console.error('SecureStorage: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      // If encryption fails, fall back to unencrypted storage
      // This is necessary for HTTP environments where Web Crypto API is not available
      console.warn(`SecureStorage: Encryption failed for ${key}, falling back to unencrypted storage:`, error instanceof Error ? error.message : String(error));
      sessionStorage.setItem(key, value);

      // Update token cache
      if (key === 'auth_token') {
        this.tokenCache.authToken = value;
      }
    }
  }

  /**
   * Retrieves and decrypts data from sessionStorage
   * Falls back to unencrypted data if encryption is not available
   */
  public async getSessionItem(key: string): Promise<string | null> {
    try {
      // If encryption is not available, only check unencrypted storage
      if (!this.isWebCryptoAvailable()) {
        const unencrypted = sessionStorage.getItem(key);
        // Update cache if this is a token
        if (key === 'auth_token' && unencrypted) {
          this.tokenCache.authToken = unencrypted;
        }
        return unencrypted;
      }

      const encryptedData = sessionStorage.getItem(`encrypted_${key}`);

      if (!encryptedData) {
        // Check for unencrypted fallback
        const unencrypted = sessionStorage.getItem(key);
        // Update cache if this is a token
        if (key === 'auth_token' && unencrypted) {
          this.tokenCache.authToken = unencrypted;
        }
        return unencrypted;
      }

      try {
        const parsed = JSON.parse(encryptedData);

        // Check if we have a token hash - if we do and decryption fails, it's old data
        const tokenHash = sessionStorage.getItem('_auth_token_hash') ||
                         sessionStorage.getItem('_refresh_token_hash') ||
                         localStorage.getItem('_token_hash');
        const hasUnencrypted = !!sessionStorage.getItem(key);

        // If we have token hash but no unencrypted data, suppress error logging
        // (we'll handle it gracefully by clearing old data)
        const suppressErrorLog = !!(tokenHash && !hasUnencrypted);

        const decrypted = await this.decrypt(parsed.encrypted, parsed.iv, suppressErrorLog);
        // Update cache if this is a token
        if (key === 'auth_token' && decrypted) {
          this.tokenCache.authToken = decrypted;
        }
        return decrypted;
      } catch (decryptError) {
        // Check if this is likely due to key mismatch (old encrypted data)
        const tokenHash = sessionStorage.getItem('_auth_token_hash') ||
                         sessionStorage.getItem('_refresh_token_hash') ||
                         localStorage.getItem('_token_hash');
        const hasUnencrypted = !!sessionStorage.getItem(key);

        // Viewer session keys that should have unencrypted fallbacks
        const viewerSessionKeys = ['currentJobId', 'currentStudyUid', 'id', 'lastViewedStudyInstanceUIDs'];

        // First, try unencrypted fallback (especially important for viewer session keys)
        const unencrypted = sessionStorage.getItem(key);
        if (unencrypted) {
          // console.log(`SecureStorage: Using unencrypted ${key} as fallback after decryption failure`);
          // Update cache if this is a token
          if (key === 'auth_token') {
            this.tokenCache.authToken = unencrypted;
          }
          return unencrypted;
        }

        // If we have a token hash but no unencrypted data and this is not a viewer session key,
        // the data was encrypted with a different key - clear ALL old encrypted data to prevent future errors
        if (tokenHash && !hasUnencrypted && !viewerSessionKeys.includes(key)) {
          console.warn(`SecureStorage: Detected key mismatch for ${key} - clearing all old encrypted data (this is normal after code updates)`);
          this.clearAllOldEncryptedData();
          return null;
        }

        // For viewer session keys, if we don't have unencrypted fallback, log warning but don't clear
        // (they should have been stored with fallback, so this indicates a different issue)
        if (viewerSessionKeys.includes(key)) {
          console.warn(`SecureStorage: Failed to decrypt ${key} and no unencrypted fallback found. This may indicate a storage issue.`);
        } else {
          // Otherwise, log the warning for other decryption failures
          console.warn(`SecureStorage: Failed to decrypt ${key}, no unencrypted fallback available:`, decryptError);
        }

        // No data available (neither encrypted nor unencrypted)
        return null;
      }
    } catch (error) {
      console.error(`SecureStorage: Failed to retrieve session item ${key}:`, error);
      // Fallback: try unencrypted
      const unencrypted = sessionStorage.getItem(key);
      if (key === 'auth_token' && unencrypted) {
        this.tokenCache.authToken = unencrypted;
      }
      return unencrypted;
    }
  }

  /**
   * Removes encrypted data from sessionStorage
   */
  public removeSessionItem(key: string): void {
    sessionStorage.removeItem(`encrypted_${key}`);
    // Also remove unencrypted fallback if exists
    sessionStorage.removeItem(key);
    // Remove token hash if this is a token
    if (key === 'auth_token' || key === 'refresh_token') {
      sessionStorage.removeItem(`_${key}_hash`);
    }
    // Clear cache if this is a token
    if (key === 'auth_token') {
      this.tokenCache.authToken = null;
    }
  }

  /**
   * Stores encrypted data in localStorage
   * Falls back to unencrypted storage if Web Crypto API is not available
   */
  public async setLocalItem(key: string, value: string): Promise<void> {
    // Check if Web Crypto API is available
    if (!this.isWebCryptoAvailable()) {
      // Fallback to unencrypted storage when encryption is not available
      console.warn(`[SecureStorage] Storing ${key} unencrypted (Web Crypto API not available)`);
      localStorage.setItem(key, value);

      // Update token cache
      if (key === 'token') {
        this.tokenCache.localToken = value;
      }
      return;
    }

    try {
      const encrypted = await this.encrypt(value);
      const storageValue = JSON.stringify({
        encrypted: encrypted.encrypted,
        iv: encrypted.iv,
        timestamp: Date.now(),
      });
      localStorage.setItem(`encrypted_${key}`, storageValue);

      // For tokens, store a hash for key derivation AND keep unencrypted version as fallback
      // This allows us to derive the key on refresh and restore session if encrypted data is cleared
      if (key === 'token') {
        // Store token hash for key derivation
        const tokenHash = await this.hashString(value);
        localStorage.setItem('_token_hash', tokenHash); // Store full hash
        // Keep unencrypted token as fallback for key derivation and session restoration
        localStorage.setItem(key, value);
        // console.log(`[SecureStorage] Stored token hash and unencrypted fallback for key derivation: ${key}`);
      } else {
        // For non-token sensitive data, remove unencrypted version after encryption
        const unencryptedValue = localStorage.getItem(key);
        if (unencryptedValue && unencryptedValue !== value) {
          // Only remove if it's different (might be old unencrypted data)
          localStorage.removeItem(key);
        }
      }

      // Update token cache for synchronous access
      if (key === 'token') {
        this.tokenCache.localToken = value;
      }

      // console.log(`[SecureStorage] Successfully stored encrypted local item: ${key}`);
    } catch (error) {
      console.error(`SecureStorage: Failed to store encrypted local item ${key}:`, error);
      console.error('SecureStorage: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      // If encryption fails, fall back to unencrypted storage
      // This is necessary for HTTP environments where Web Crypto API is not available
      console.warn(`SecureStorage: Encryption failed for ${key}, falling back to unencrypted storage:`, error instanceof Error ? error.message : String(error));
      localStorage.setItem(key, value);

      // Update token cache
      if (key === 'token') {
        this.tokenCache.localToken = value;
      }
    }
  }

  /**
   * Retrieves and decrypts data from localStorage
   * Falls back to unencrypted data if encryption is not available
   */
  public async getLocalItem(key: string): Promise<string | null> {
    try {
      // If encryption is not available, only check unencrypted storage
      if (!this.isWebCryptoAvailable()) {
        const unencrypted = localStorage.getItem(key);
        // Update cache if this is a token
        if (key === 'token' && unencrypted) {
          this.tokenCache.localToken = unencrypted;
        }
        return unencrypted;
      }

      const encryptedData = localStorage.getItem(`encrypted_${key}`);

      if (!encryptedData) {
        // Check for unencrypted fallback
        const unencrypted = localStorage.getItem(key);
        // Update cache if this is a token
        if (key === 'token' && unencrypted) {
          this.tokenCache.localToken = unencrypted;
        }
        return unencrypted;
      }

      try {
        const parsed = JSON.parse(encryptedData);
        const decrypted = await this.decrypt(parsed.encrypted, parsed.iv);
        // Update cache if this is a token
        if (key === 'token' && decrypted) {
          this.tokenCache.localToken = decrypted;
        }
        return decrypted;
      } catch (decryptError) {
        // If decryption fails, try unencrypted
        console.warn(`SecureStorage: Failed to decrypt ${key}, trying unencrypted fallback:`, decryptError);
        const unencrypted = localStorage.getItem(key);
        if (unencrypted) {
          // Update cache if this is a token
          if (key === 'token') {
            this.tokenCache.localToken = unencrypted;
          }
          return unencrypted;
        }
        return null;
      }
    } catch (error) {
      console.error(`SecureStorage: Failed to retrieve encrypted local item ${key}:`, error);
      // Fallback: try unencrypted
      const unencrypted = localStorage.getItem(key);
      if (key === 'token' && unencrypted) {
        this.tokenCache.localToken = unencrypted;
      }
      return unencrypted;
    }
  }

  /**
   * Removes encrypted data from localStorage
   */
  public removeLocalItem(key: string): void {
    localStorage.removeItem(`encrypted_${key}`);
    // Also remove unencrypted fallback if exists
    localStorage.removeItem(key);
    // Remove token hash if this is a token
    if (key === 'token') {
      localStorage.removeItem('_token_hash');
    }
    // Clear cache if this is a token
    if (key === 'token') {
      this.tokenCache.localToken = null;
    }
  }

  /**
   * Clears the encryption key (should be called on logout)
   */
  public clearEncryptionKey(): void {
    this.encryptionKey = null;
    this.keyDerivationSalt = null;
    this.tokenCache.authToken = null;
    this.tokenCache.localToken = null;
  }

  /**
   * Gets the cached token synchronously (for use in synchronous contexts like DICOM Web client)
   * This returns the token from cache, which is updated when tokens are stored
   */
  public getCachedToken(): string | null {
    return this.tokenCache.localToken || this.tokenCache.authToken;
  }

  /**
   * Migrates existing unencrypted data to encrypted storage
   * Call this during app initialization to encrypt existing data
   * Skips migration if encryption is not available
   */
  public async migrateToEncrypted(key: string, useLocalStorage: boolean = false): Promise<void> {
    // Skip migration if encryption is not available
    if (!this.isWebCryptoAvailable()) {
      // console.log(`SecureStorage: Skipping migration of ${key} (encryption not available)`);
      return;
    }

    const storage = useLocalStorage ? localStorage : sessionStorage;
    const value = storage.getItem(key);

    if (value && !value.startsWith('{"encrypted"')) {
      // Data is not encrypted, migrate it
      try {
        if (useLocalStorage) {
          await this.setLocalItem(key, value);
        } else {
          await this.setSessionItem(key, value);
        }
        // Remove old unencrypted data only after successful encryption
        // Note: setSessionItem/setLocalItem will keep unencrypted version for tokens
        // So we only remove for non-token data
        const tokenKeys = ['auth_token', 'refresh_token', 'token'];
        if (!tokenKeys.includes(key)) {
          storage.removeItem(key);
        }
        // console.log(`SecureStorage: Migrated ${key} to encrypted storage`);
      } catch (error) {
        console.error(`SecureStorage: Failed to migrate ${key}:`, error);
        // Don't remove unencrypted data if migration failed
        throw error;
      }
    }
  }

  /**
   * Initializes encryption and ensures it's ready
   * Call this during app initialization to verify encryption is working
   * Returns false if Web Crypto API is not available (e.g., HTTP environment)
   */
  public async initializeEncryption(): Promise<boolean> {
    try {
      // Check if Web Crypto API is available
      if (!this.isWebCryptoAvailable()) {
        console.warn('[SecureStorage] Web Crypto API is not available - encryption disabled (this is normal for HTTP environments)');
        return false;
      }

      // Test encryption by deriving a key
      const testKey = await this.getEncryptionKey();
      if (!testKey) {
        console.error('SecureStorage: Failed to derive encryption key');
        return false;
      }

      // Test encryption/decryption with a small test value
      const testValue = 'test_encryption';
      const encrypted = await this.encrypt(testValue);
      const decrypted = await this.decrypt(encrypted.encrypted, encrypted.iv);

      if (decrypted !== testValue) {
        console.error('SecureStorage: Encryption test failed - decrypted value does not match');
        return false;
      }

      // console.log('[SecureStorage] Encryption initialized and verified successfully');
      return true;
    } catch (error) {
      console.error('SecureStorage: Encryption initialization failed:', error);
      return false;
    }
  }

  /**
   * Migrates all unencrypted sensitive data to encrypted storage
   * Call this during app initialization after login
   */
  public async migrateAllSensitiveData(): Promise<void> {
    const sensitiveSessionKeys = ['auth_token', 'refresh_token', 'ohif_session', 'idle_expiry'];
    const sensitiveLocalKeys = ['token'];

    // console.log('[SecureStorage] Starting migration of sensitive data to encrypted storage...');

    // Migrate sessionStorage items
    for (const key of sensitiveSessionKeys) {
      try {
        await this.migrateToEncrypted(key, false);
      } catch (error) {
        console.warn(`SecureStorage: Could not migrate ${key} from sessionStorage:`, error);
      }
    }

    // Migrate localStorage items
    for (const key of sensitiveLocalKeys) {
      try {
        await this.migrateToEncrypted(key, true);
      } catch (error) {
        console.warn(`SecureStorage: Could not migrate ${key} from localStorage:`, error);
      }
    }

    // console.log('[SecureStorage] Migration of sensitive data completed');
  }

  /**
   * Checks if encryption is working properly
   */
  public async isEncryptionAvailable(): Promise<boolean> {
    // First check if Web Crypto API is available
    if (!this.isWebCryptoAvailable()) {
      return false;
    }

    try {
      const testKey = await this.getEncryptionKey();
      return testKey !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clears all old encrypted data that cannot be decrypted
   * Called when key mismatch is detected
   * Preserves tokens if unencrypted versions exist
   */
  private clearAllOldEncryptedData(): void {
    console.warn('[SecureStorage] Clearing all old encrypted data due to key mismatch');

    // Check for unencrypted tokens before clearing encrypted ones
    const hasUnencryptedAuthToken = !!sessionStorage.getItem('auth_token');
    const hasUnencryptedLocalToken = !!localStorage.getItem('token');
    const hasUnencryptedRefreshToken = !!sessionStorage.getItem('refresh_token');

    // Clear all encrypted session items
    const encryptedSessionKeys = [
      'encrypted_ohif_session',
      'encrypted_idle_expiry',
      'encrypted_currentJobId',
      'encrypted_currentStudyUid',
      'encrypted_id',
      'encrypted_lastViewedStudyInstanceUIDs',
      'encrypted_currentSubmissionId',
      'encrypted_currentSubmissionProtocol'
    ];

    encryptedSessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Only clear encrypted tokens if we have unencrypted versions to fall back to
    // This preserves tokens for session restoration
    if (hasUnencryptedAuthToken) {
      sessionStorage.removeItem('encrypted_auth_token');
      // console.log('[SecureStorage] Cleared encrypted auth_token (unencrypted version exists)');
    } else {
      console.warn('[SecureStorage] Keeping encrypted auth_token (no unencrypted fallback)');
    }

    if (hasUnencryptedRefreshToken) {
      sessionStorage.removeItem('encrypted_refresh_token');
      // console.log('[SecureStorage] Cleared encrypted refresh_token (unencrypted version exists)');
    } else {
      console.warn('[SecureStorage] Keeping encrypted refresh_token (no unencrypted fallback)');
    }

    if (hasUnencryptedLocalToken) {
      localStorage.removeItem('encrypted_token');
      // console.log('[SecureStorage] Cleared encrypted token (unencrypted version exists)');
    } else {
      console.warn('[SecureStorage] Keeping encrypted token (no unencrypted fallback)');
    }

    // Note: We keep the token hashes and salt so we can still derive the key correctly
    // The hashes will be regenerated on next login
  }
}

export default SecureStorageService;
